import { useEffect, useState } from "react";
import { api, fmtDate, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { LifeBuoy } from "lucide-react";

const chip = (s) => (s === "Resolved" ? "bg-emerald-100 text-emerald-700"
  : s === "In Progress" ? "bg-sky-100 text-sky-700" : "bg-amber-100 text-amber-700");

export default function PortalSupport() {
  const [tickets, setTickets] = useState([]);
  const [form, setForm] = useState({ subject: "", kind: "question", message: "", file: null });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/portal/support").then((r) => setTickets(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.subject.trim()) { toast.error("Enter a subject"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("subject", form.subject);
      fd.append("kind", form.kind);
      fd.append("message", form.message);
      if (form.file) fd.append("file", form.file);
      await api.post("/portal/support", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Request submitted — our team will respond soon");
      setForm({ subject: "", kind: "question", message: "", file: null });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <div className="grid lg:grid-cols-2 gap-5 items-start" data-testid="portal-support">
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 font-semibold mb-4">
          <LifeBuoy size={13} className="text-[#FC5F5F]" /> Raise a Request</div>
        <div className="space-y-3 text-sm">
          <div className="flex gap-2">
            {[["question", "Question"], ["complaint", "Complaint"], ["document", "Document Request"]].map(([v, lab]) => (
              <button key={v} onClick={() => setForm({ ...form, kind: v })}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors duration-200 ${form.kind === v ? "bg-[#3B82F6] text-white border-[#3B82F6]" : "border-slate-200 text-slate-600"}`}
                      data-testid={`ps-kind-${v}`}>{lab}</button>
            ))}
          </div>
          <input className="w-full border border-slate-200 rounded-md px-3 py-2" placeholder="Subject"
                 value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="ps-subject" />
          <textarea rows={4} className="w-full border border-slate-200 rounded-md px-3 py-2" placeholder="Describe your request…"
                    value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="ps-message" />
          <input type="file" className="w-full text-xs" accept=".pdf,image/*"
                 onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} data-testid="ps-file" />
          <button onClick={submit} disabled={busy} className="w-full bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-md py-2.5 text-sm font-semibold disabled:opacity-50" data-testid="ps-submit">
            {busy ? "Submitting…" : "Submit Request"}</button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">Your Requests</div>
        <div className="space-y-3">
          {tickets.map((t) => (
            <div key={t.id} className="border border-slate-100 rounded-lg p-3 text-[13px]">
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-slate-900">{t.subject}</span>
                <span className={`text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 ${chip(t.status)}`}>{t.status}</span>
              </div>
              {t.message && <p className="text-slate-500 text-xs mt-1">{t.message}</p>}
              {t.reply && <p className="text-xs mt-2 bg-slate-50 border border-slate-100 rounded p-2"><span className="font-semibold text-[#3B82F6]">Hi-Tech Silico:</span> {t.reply}</p>}
              <div className="text-[10px] text-slate-400 mt-1.5">{fmtDate(t.created_at)}</div>
            </div>
          ))}
          {tickets.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">No requests yet.</p>}
        </div>
      </div>
    </div>
  );
}
